import React, { memo, useContext } from "react";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { Field } from "redux-form";

import {
	Button,
	RequiredMarkContext,
	SpinnerLoader,
	PhoneInputRedux,
	Badge,
	AddressSectionRedux,
	CheckButton,
	CustomInput,
	OverlayTrigger,
} from "components";
import { MerchantSensitiveDataContext } from "components/contexts";
import {
	float2Normalizer,
	SSNNormalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import { maxPhoneLength15, phoneMinLength8 } from "validators/userValidators";
import {
	dateOfBirth1900,
	dateOfBirthMoreThan18,
	dateValidation,
	emailValid,
	floatValidation,
	maxLength30,
	maxLengthSSN,
	maxValue100,
	minLength2,
	minLengthSSN,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyDate,
	onlyPercent,
	required,
	totalPercentageOfOwnership,
} from "validators/validation";

import { PoliticallyExposedPersonTitle } from "./ownerHelpers";
import { uniqueSsn } from "./validators";

import styles from "../formSections.module.scss";

const Owner = ({
	owner,
	index,
	ownersValidationInfo,
	removeOwner,
	allLoading,
	hiddenFieldsOnMPA,
	isPaperApplication,
	formName,
	isBadgeShown = false,
	sectionName,
}) => {
	const isRequired = useContext(RequiredMarkContext);
	const { isReadOnly, isEditMode } = useContext(MerchantSensitiveDataContext);

	return owner ? (
		<>
			<Row>
				<Col>
					<div className={styles.wrappOwner}>
						<p>
							{`Owner ${index + 1}`}{" "}
							{isBadgeShown && (
								<Badge
									variant="primary"
									value="Signature is required"
									className="ml-3"
								/>
							)}
						</p>
						{index === 0 ? null : (
							<Button
								value="Remove owner"
								onClick={() => removeOwner(index)}
								variant="secondary"
								style={{ marginRight: "-21px" }}
							/>
						)}
					</div>
				</Col>
			</Row>
			<Row className={`${styles.wrappCheckboxes} pb-1`}>
				{index === 0 ? (
					<Col md={5}>
						<Field
							component={CheckButton}
							label="Is an individual with significant responsibility"
							key={`personResponsibility${index}`}
							id={`personResponsibility${index}`}
							name={`${owner}.isAnIndividualWithSignificantResponsibility`}
							checked={
								owner.isAnIndividualWithSignificantResponsibility
							}
							blurLoading={allLoading}
							skeletonWidth="340px"
							hideRequiredMark
						/>
					</Col>
				) : null}
				<Col md={{ span: 5, offset: index === 0 ? 2 : 0 }}>
					<div className="d-flex">
						<Field
							component={CheckButton}
							key={`personPolitically${index}`}
							id={`personPolitically${index}`}
							label="Is a Politically Exposed Person"
							name={`${owner}.isAPoliticallyExposedPerson`}
							checked={owner.isAPoliticallyExposedPerson}
							blurLoading={allLoading}
							skeletonWidth="270px"
							hideRequiredMark
						/>
						{allLoading ? null : (
							<OverlayTrigger
								title={PoliticallyExposedPersonTitle}
							/>
						)}
					</div>
				</Col>
			</Row>
			<Row className="pt-3">
				<Col md={5}>
					<Field
						component={CustomInput}
						key={`firstName${index}`}
						id={`firstName${index}`}
						validate={fp.compact([
							isRequired ? required : undefined,
							minLength2,
							maxLength30,
						])}
						loader={SpinnerLoader}
						label="First Name"
						name={`${owner}.firstName`}
						disabled={
							ownersValidationInfo[index]?.isSsnBeingValidated
						}
						blurLoading={allLoading}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						readOnly={isEditMode && isReadOnly}
						customError={
							ownersValidationInfo[index]?.ssnValidationError
						}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						key={`lastName${index}`}
						id={`lastName${index}`}
						validate={fp.compact([
							isRequired ? required : undefined,
							minLength2,
							maxLength30,
						])}
						label="Last Name"
						loader={SpinnerLoader}
						name={`${owner}.lastName`}
						disabled={
							ownersValidationInfo[index]?.isSsnBeingValidated
						}
						blurLoading={allLoading}
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						readOnly={isEditMode && isReadOnly}
						customError={
							ownersValidationInfo[index]?.ssnValidationError
						}
					/>
				</Col>
			</Row>
			{!hiddenFieldsOnMPA?.includes("titlePosition&Ownership") && (
				<>
					<Row>
						<Col md={5}>
							<Field
								component={CustomInput}
								validate={fp.compact([
									isRequired && !isPaperApplication
										? required
										: undefined,
									minLength2,
									maxLength30,
								])}
								label="Title / Position"
								name={`${owner}.titlePosition`}
								blurLoading={allLoading}
								normalizeOnBlur={trimNormalizer}
								normalize={normalizeAll([
									onlyAlphanumericAndSpecialCharacters,
									trimMidSentenceNormalizer,
								])}
								hideRequiredMark={
									!(isRequired && !isPaperApplication)
								}
							/>
						</Col>
						<Col md={{ span: 5, offset: 2 }}>
							<Field
								component={CustomInput}
								key={`ownership${index}`}
								normalizeOnBlur={float2Normalizer}
								id={`ownership${index}`}
								validate={fp.compact([
									isRequired && !isPaperApplication
										? required
										: undefined,
									floatValidation,
									maxValue100,
									totalPercentageOfOwnership,
								])}
								label="Ownership, %"
								normalize={onlyPercent}
								name={`${owner}.ownership`}
								blurLoading={allLoading}
								hideRequiredMark={
									!(isRequired && !isPaperApplication)
								}
							/>
						</Col>
					</Row>
				</>
			)}

			<Row>
				<Col md={5}>
					<Field
						component={PhoneInputRedux}
						label="Home / Mobile Phone"
						name={`${owner}.homeMobilePhone`}
						validate={fp.compact([
							isRequired ? required : undefined,
							phoneMinLength8,
							maxPhoneLength15,
						])}
						isRequired={isRequired}
						formName={formName}
						blurLoading={allLoading}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						validate={fp.compact([
							isRequired ? required : undefined,
							emailValid,
						])}
						label="Email Address"
						name={`${owner}.emailAddress`}
						blurLoading={allLoading}
						normalizeOnBlur={trimNormalizer}
						normalize={trimMidSentenceNormalizer}
					/>
				</Col>
			</Row>

			<AddressSectionRedux
				formName={formName}
				sectionName={`${sectionName}.${owner}.address`}
				sectionSubName={`${owner}`}
				isRequired={isRequired}
				isLoading={allLoading}
			/>

			<Row>
				<Col md={5}>
					<Field
						component={CustomInput}
						validate={
							!isReadOnly &&
							!isEditMode &&
							fp.compact([
								isRequired ? required : undefined,
								minLengthSSN,
								maxLengthSSN,
								uniqueSsn,
							])
						}
						label="SSN"
						normalize={SSNNormalizer}
						loader={SpinnerLoader}
						name={`${owner}.ssn`}
						loading={
							ownersValidationInfo[index]?.isSsnBeingValidated
						}
						customError={
							ownersValidationInfo[index]?.ssnValidationError
						}
						disabled={
							ownersValidationInfo[index]?.isSsnBeingValidated
						}
						blurLoading={allLoading}
						disableTriggerSave={!uniqueSsn}
						readOnly={isEditMode && isReadOnly}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						validate={fp.compact([
							isRequired ? required : undefined,
							dateValidation,
							dateOfBirth1900,
							dateOfBirthMoreThan18,
						])}
						normalize={onlyDate}
						index={`${index}`}
						key={`dateOfBirth${index}`}
						id={`dateOfBirth${index}`}
						name={`${owner}.dateOfBirth`}
						label="Date of Birth"
						blurLoading={allLoading}
					/>
				</Col>
			</Row>
		</>
	) : null;
};

export default memo(Owner);
